import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const IRAPI = createApi({
	reducerPath: "IR",
	baseQuery: fetchBaseQuery({
		baseUrl: process.env.REACT_APP_BACKEND_URL,
		credentials: "include",
	}),
	// ✅ Add tag types
	tagTypes: ["IR", "IRList", "PendingReviews", "Statistics"],

	endpoints: (builder) => ({
		getAllIRs: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const sortOrder = val.sortorder ? `&sortOrder=${val.sortorder}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				const status = val.status ? `&status=${val.status}` : "";
				const queryString = val.queryString
					? `&${val.queryString.substring(1)}`
					: "";
				return `/ir?page=${page}&limit=${limit}${search}${sortBy}${sortOrder}${queryString}${status}`;
			},
			// ✅ Provide tags for this query
			providesTags: (result) =>
				result?.data
					? [
							...result.data.map(({ id }) => ({ type: "IR", id })),
							{ type: "IRList", id: "LIST" },
						]
					: [{ type: "IRList", id: "LIST" }],
		}),

		getIRenums: builder.query({
			query: () => `ir/enums`,
		}),

		getSingleIR: builder.query({
			query: (id) => `/ir/${id}`,
			// ✅ Provide tag for single IR
			providesTags: (result, error, id) => [{ type: "IR", id }],
		}),

		addIR: builder.mutation({
			query: (val) => {
				const formData = new FormData();

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						if (data[key] === null || data[key] === undefined) continue;

						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];

						if (Array.isArray(value)) {
							if (value.length > 0) {
								if (value[0] instanceof File) {
									value.forEach((file) => formData.append(fullKey, file));
								} else if (typeof value[0] === "object") {
									value.forEach((item, index) => {
										appendFormData(item, `${fullKey}[${index}]`);
									});
								} else {
									formData.append(fullKey, JSON.stringify(value));
								}
							} else {
								formData.append(fullKey, JSON.stringify([]));
							}
						} else if (typeof value === "object" && !(value instanceof File)) {
							appendFormData(value, fullKey);
						} else {
							formData.append(fullKey, value);
						}
					}
				};

				appendFormData(val);

				return {
					url: `/ir`,
					method: "POST",
					body: formData,
				};
			},
			// ✅ Invalidate cache after creating IR
			invalidatesTags: [{ type: "IRList", id: "LIST" }, "Statistics"],
		}),

		editIR: builder.mutation({
			query: ({ id, val }) => {
				const formData = new FormData();

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						if (data[key] === null || data[key] === undefined) continue;

						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];

						if (Array.isArray(value)) {
							if (value.length > 0) {
								if (value[0] instanceof File) {
									value.forEach((file) => formData.append(fullKey, file));
								} else if (typeof value[0] === "object") {
									value.forEach((item, index) => {
										appendFormData(item, `${fullKey}[${index}]`);
									});
								} else {
									formData.append(fullKey, JSON.stringify(value));
								}
							} else {
								formData.append(fullKey, JSON.stringify([]));
							}
						} else if (typeof value === "object" && !(value instanceof File)) {
							appendFormData(value, fullKey);
						} else {
							formData.append(fullKey, value);
						}
					}
				};

				// Handle removed_attachments - always send when present (array or JSON string)
				const removedAttachments = val.removed_attachments;
				if (removedAttachments !== undefined && removedAttachments !== null) {
					if (Array.isArray(removedAttachments)) {
						formData.append(
							"removed_attachments",
							JSON.stringify(removedAttachments),
						);
					} else {
						formData.append("removed_attachments", removedAttachments);
					}
				}
				delete val.removed_attachments;

				// Handle edit_notes (also accepted as correction_notes)
				if (val.edit_notes) {
					formData.append("edit_notes", val.edit_notes);
					delete val.edit_notes;
				}
				if (val.correction_notes) {
					formData.append("correction_notes", val.correction_notes);
					delete val.correction_notes;
				}

				// For edit: only send new File objects in upload fields; do not re-send existing attachment identifiers
				if (val.fields && Array.isArray(val.fields)) {
					val.fields = val.fields.map((field) => {
						if (!field || !Array.isArray(field.value)) return field;
						const onlyNewFiles = field.value.filter((v) => v instanceof File);
						return { ...field, value: onlyNewFiles };
					});
				}

				appendFormData(val);

				return {
					url: `/ir/${id}/revision/edit`,
					method: "PATCH",
					body: formData,
				};
			},
			// ✅ Invalidate cache after updating IR
			invalidatesTags: (result, error, { id }) => [
				{ type: "IR", id },
				{ type: "IRList", id: "LIST" },
			],
		}),

		resubmitIR: builder.mutation({
			query: ({ id, val }) => {
				const formData = new FormData();

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						if (data[key] === null || data[key] === undefined) continue;

						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];

						if (Array.isArray(value)) {
							if (value.length > 0) {
								if (value[0] instanceof File) {
									value.forEach((file) => formData.append(fullKey, file));
								} else if (typeof value[0] === "object") {
									value.forEach((item, index) => {
										appendFormData(item, `${fullKey}[${index}]`);
									});
								} else {
									formData.append(fullKey, JSON.stringify(value));
								}
							} else {
								formData.append(fullKey, JSON.stringify([]));
							}
						} else if (typeof value === "object" && !(value instanceof File)) {
							appendFormData(value, fullKey);
						} else {
							formData.append(fullKey, value);
						}
					}
				};

				appendFormData(val);

				return {
					url: `/ir/${id}/resubmit`,
					method: "POST",
					body: formData,
				};
			},
			// ✅ Invalidate cache after resubmitting IR
			invalidatesTags: (result, error, { id }) => [
				{ type: "IR", id },
				{ type: "IRList", id: "LIST" },
				"PendingReviews",
			],
		}),

		qaIR: builder.mutation({
			query: ({ id, val }) => {
				return {
					url: `/ir/${id}/qa-review`,
					method: "POST",
					body: val,
				};
			},
			// ✅ Invalidate cache after QA review
			invalidatesTags: (result, error, { id }) => [
				{ type: "IR", id },
				{ type: "IRList", id: "LIST" },
				"PendingReviews",
				"Statistics",
			],
		}),

		consultantQAIR: builder.mutation({
			query: ({ id, val }) => {
				return {
					url: `/ir/${id}/consultant-review`,
					method: "POST",
					body: val,
				};
			},
			// ✅ Invalidate cache after consultant review
			invalidatesTags: (result, error, { id }) => [
				{ type: "IR", id },
				{ type: "IRList", id: "LIST" },
				"PendingReviews",
				"Statistics",
			],
		}),
		exportIRListToExcel: builder.mutation({
			queryFn: async (filters, _queryApi, _extraOptions, fetchWithBQ) => {
				try {
					const params = new URLSearchParams();
					if (filters.search) params.append("search", filters.search);
					if (filters.status) params.append("status", filters.status);
					if (filters.sortby) params.append("sortBy", filters.sortby);
					if (filters.order) params.append("order", filters.order);

					const token = localStorage.getItem("token");
					const url = `${
						process.env.REACT_APP_BACKEND_URL
					}/ir/export/complete?${params.toString()}`;

					const response = await fetch(url, {
						method: "GET",
						headers: {
							Authorization: `Bearer ${token}`,
						},
					});

					if (!response.ok) {
						throw new Error(`Export failed: ${response.statusText}`);
					}

					// ✅ FIXED: Better filename extraction
					const contentDisposition = response.headers.get(
						"Content-Disposition",
					);
					let filename = "ir-list.xlsx"; // Default fallback

					if (contentDisposition) {
						// Try to match: filename="something.xlsx" or filename=something.xlsx
						const filenameMatch = contentDisposition.match(
							/filename[^;=\n]*=["']?([^"'\n;]+)["']?/i,
						);
						if (filenameMatch && filenameMatch[1]) {
							filename = filenameMatch[1].trim();
							// Remove any trailing quotes or underscores
							filename = filename
								.replace(/^["']|["']$/g, "")
								.replace(/_+$/, "");
						}
					}

					// ✅ Final safety check - ensure proper extension
					if (!filename.endsWith(".xlsx")) {
						filename = filename.replace(/\.(xlsx|xls)?.*$/, ".xlsx");
					}

					console.log("📥 Downloading Excel file:", filename); // Debug log

					const blob = await response.blob();
					const downloadUrl = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = downloadUrl;
					link.download = filename;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(downloadUrl);

					return { data: { success: true, filename } };
				} catch (error) {
					return { error: { status: "FETCH_ERROR", error: error.message } };
				}
			},
		}),

		// ✅ Export to PDF - FIXED FILENAME EXTRACTION
		exportIRListToPdf: builder.mutation({
			queryFn: async (filters, _queryApi, _extraOptions, fetchWithBQ) => {
				try {
					const params = new URLSearchParams();
					if (filters.search) params.append("search", filters.search);
					if (filters.status) params.append("status", filters.status);
					if (filters.sortby) params.append("sortBy", filters.sortby);
					if (filters.order) params.append("order", filters.order);

					const token = localStorage.getItem("token");
					const url = `${
						process.env.REACT_APP_BACKEND_URL
					}/ir/export/pdf?${params.toString()}`;

					const response = await fetch(url, {
						method: "GET",
						headers: {
							Authorization: `Bearer ${token}`,
						},
					});

					if (!response.ok) {
						throw new Error(`Export failed: ${response.statusText}`);
					}

					// ✅ FIXED: Better filename extraction
					const contentDisposition = response.headers.get(
						"Content-Disposition",
					);
					let filename = "ir-list.pdf"; // Default fallback

					if (contentDisposition) {
						// Try to match: filename="something.pdf" or filename=something.pdf
						const filenameMatch = contentDisposition.match(
							/filename[^;=\n]*=["']?([^"'\n;]+)["']?/i,
						);
						if (filenameMatch && filenameMatch[1]) {
							filename = filenameMatch[1].trim();
							// Remove any trailing quotes or underscores
							filename = filename
								.replace(/^["']|["']$/g, "")
								.replace(/_+$/, "");
						}
					}

					// ✅ Final safety check - ensure proper extension
					if (!filename.endsWith(".pdf")) {
						filename = filename.replace(/\.(pdf)?.*$/, ".pdf");
					}

					console.log("📥 Downloading PDF file:", filename); // Debug log

					const blob = await response.blob();
					const downloadUrl = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = downloadUrl;
					link.download = filename;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(downloadUrl);

					return { data: { success: true, filename } };
				} catch (error) {
					return { error: { status: "FETCH_ERROR", error: error.message } };
				}
			},
		}),
		getOfflineIRs: builder.query({
			query: (val) => {
				const queryString = val.queryString;
				return `/ir?${queryString}`;
			},
		}),
	}),
});

export const {
	useGetAllIRsQuery,
	useGetIRenumsQuery,
	useGetSingleIRQuery,
	useAddIRMutation,
	useEditIRMutation,
	useResubmitIRMutation,
	useQaIRMutation,
	useConsultantQAIRMutation,
	// ✅ Export mutations
	useExportIRListToExcelMutation,
	useExportIRListToPdfMutation,
	useLazyGetOfflineIRsQuery,
	// ✅ Export util for manual cache updates
	util: { invalidateTags, updateQueryData },
} = IRAPI;
